import React, { useState } from 'react';
import { X } from '../lib/icons';
import { supabase } from '../lib/supabase';
import {
  DEFAULT_VISIT_DURATION_BY_TYPE,
  VISIT_DURATION_OPTIONS_MINUTES,
  formatVisitDurationLabel,
  VisitType,
} from '../lib/visitDuration';

interface Props {
  homeId: string;
  homeAddress: string;
  ownerName?: string;
  isOpen: boolean;
  defaultVisitType?: VisitType;
  onClose: () => void;
  /** Called with the new visit's ID after a successful save. */
  onScheduled: (newVisitId: string) => void;
}

const INITIAL_FORM = (defaultVisitType: VisitType) => ({
  visit_type: defaultVisitType,
  scheduled_date: '',
  estimated_duration_minutes: DEFAULT_VISIT_DURATION_BY_TYPE[defaultVisitType],
  assigned_to: 'internal',
  internal_name: '',
  visit_notes: '',
});

export const ScheduleVisitModal: React.FC<Props> = ({
  homeId,
  homeAddress,
  ownerName,
  isOpen,
  defaultVisitType = 'maintenance',
  onClose,
  onScheduled,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM(defaultVisitType));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFormData(INITIAL_FORM(defaultVisitType));
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: visitData, error: visitError } = await supabase
        .from('visits')
        .insert([
          {
            home_id: homeId,
            visit_type: formData.visit_type,
            scheduled_date: new Date(formData.scheduled_date).toISOString(),
            estimated_duration_minutes: formData.estimated_duration_minutes,
            assigned_to: formData.assigned_to,
            internal_name: formData.internal_name.trim() ? formData.internal_name.trim() : null,
            visit_notes: formData.visit_notes || null,
            created_by: user.id,
            updated_by: user.id,
          },
        ])
        .select('id')
        .single();

      if (visitError) throw visitError;

      reset();
      onScheduled(visitData.id);
    } catch (err: any) {
      console.error('Error scheduling visit:', err);
      setError(err.message ?? 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Schedule Visit</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Home info */}
        <div className="px-5 pt-4 pb-2">
          <div className="rounded-lg bg-gray-50 px-4 py-3">
            <p className="text-sm font-medium text-gray-900">{homeAddress}</p>
            {ownerName && <p className="text-xs text-gray-500 mt-0.5">Owner: {ownerName}</p>}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Internal name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Internal Name <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.internal_name}
              maxLength={120}
              onChange={(e) => setFormData({ ...formData, internal_name: e.target.value })}
              placeholder="e.g. Smith — Furnace tune-up"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Friendly label to help admins distinguish similar visits. Not shown to homeowners or contractors.
            </p>
          </div>

          {/* Visit type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type</label>
            <select
              value={formData.visit_type}
              onChange={(e) => {
                const visitType = e.target.value as VisitType;
                setFormData({
                  ...formData,
                  visit_type: visitType,
                  estimated_duration_minutes: DEFAULT_VISIT_DURATION_BY_TYPE[visitType],
                });
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="service">Service Visit</option>
              <option value="maintenance">Maintenance Visit</option>
              <option value="walkthrough">Walkthrough</option>
            </select>
          </div>

          {/* Date & time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scheduled Date & Time
            </label>
            <input
              type="datetime-local"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={formData.estimated_duration_minutes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  estimated_duration_minutes: parseInt(e.target.value, 10),
                })
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {VISIT_DURATION_OPTIONS_MINUTES.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {formatVisitDurationLabel(minutes)}
                </option>
              ))}
            </select>
          </div>

          {/* Assigned to */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
            <input
              type="text"
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              placeholder="internal, contractor name, etc."
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Visit Notes{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={formData.visit_notes}
              onChange={(e) => setFormData({ ...formData, visit_notes: e.target.value })}
              rows={3}
              placeholder="Special instructions or notes…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-xl bg-black py-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Scheduling…' : 'Schedule Visit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
